'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { X, BookOpen, Layers, Play, Map, User } from 'lucide-react';
import { StreakDisplay } from '@/components/gamification/StreakDisplay';
import { XPBar } from '@/components/gamification/XPBar';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const NAV_LINKS = [
  { href: '/questions', label: 'Questions', icon: BookOpen },
  { href: '/categories', label: 'Categories', icon: Layers },
  { href: '/quiz', label: 'Quiz Mode', icon: Play },
  { href: '/roadmap', label: 'Roadmap', icon: Map },
  { href: '/profile', label: 'My Profile', icon: User },
];

export function MobileMenu({ isOpen, onClose }: Props) {
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Navigation menu"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(8,8,16,0.8)', backdropFilter: 'blur(4px)' }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="absolute top-0 right-0 h-full w-72 flex flex-col"
        style={{
          background: 'var(--color-surface)',
          borderLeft: '1px solid var(--color-border)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid var(--color-border)' }}>
          <span className="font-display font-bold text-lg gradient-text">dartdosage</span>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--color-text-secondary)' }}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* XP status */}
        <div className="p-5" style={{ borderBottom: '1px solid var(--color-border)' }}>
          <XPBar compact />
          <div className="mt-3">
            <StreakDisplay />
          </div>
        </div>

        {/* Nav links */}
        <nav className="flex-1 p-5 space-y-1">
          {NAV_LINKS.map(({ href, label, icon: Icon }) => (
            <Link
              key={href}
              href={href}
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all"
              style={{ color: 'var(--color-text-secondary)' }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(91,79,232,0.1)';
                (e.currentTarget as HTMLAnchorElement).style.color = 'var(--color-primary)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                (e.currentTarget as HTMLAnchorElement).style.color = 'var(--color-text-secondary)';
              }}
            >
              <Icon size={18} />
              {label}
            </Link>
          ))}
        </nav>

        {/* CTA */}
        <div className="p-5" style={{ borderTop: '1px solid var(--color-border)' }}>
          <Link href="/quiz" onClick={onClose} className="btn-primary w-full text-center">
            Start Quiz
          </Link>
        </div>
      </div>
    </div>
  );
}
